import { useCallback } from 'react';
import { ApiParams } from '../../common/api/api';
import {
  ApiResult,
  useApiGet,
  useApiPost,
  useApiPatch
} from '../../common/api/Api.hooks';
import { useDownloadBlob } from '../../common/blob.hook';
import { useIdSearchableFetch } from '../../common/entityId.hooks';
import { PaginatedList } from '../../common/PaginatedList.interface';
import { Task } from '../tasks/Task.interface';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import { Risk, CreateRiskDto, UpdateRiskDto } from './Risk.interface';

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function useFindRisks() {
  const { data, isLoading, error, get } =
    useApiGet<PaginatedList<Risk>>('risks');
  const findRisks = useIdSearchableFetch(get, WorkflowEntity.Risk);
  return { riskList: data, isLoading, error, get: findRisks };
}

export function useFindRisk(id: number) {
  const { data, isLoading, error, get } = useApiGet<Risk>('risks/' + id);
  return { data, isLoading, error, get };
}

export function useFindRiskByTask(taskId: number) {
  const { data, isLoading, error, get } = useApiGet<Risk>(
    'risks/task/' + taskId
  );
  return { data, isLoading, error, get };
}

export function useFindRiskReferences(riskId: number, target: string) {
  const { data, isLoading, error, get } = useApiGet<
    { id: number; title: string; task: Task }[]
  >(`risks/${riskId}/${target}`);
  return {
    references: data,
    isLoadingReferences: isLoading,
    loadingReferenceError: error,
    findReferences: get
  };
}

export function useAssignRiskReferences(riskId: number, target: string) {
  const { data, isLoading, error, post } = useApiPost<{
    id: number;
    title: string;
    task: Task;
  }>(`risks/${riskId}/${target}`);
  const assignReferences = useCallback(
    (targetIds: number[]) => {
      post({ targetIds });
    },
    [post]
  );
  return {
    updatedSource: data,
    isAssigningReferences: isLoading,
    assigningReferenceError: error,
    assignReferences
  };
}

export function useCreateRisk(): [
  ApiResult<Risk>,
  (dto: CreateRiskDto) => void
] {
  const { post: apiPost, ...result } = useApiPost<Risk>('risks');
  const post = (dto: CreateRiskDto) => apiPost(dto);
  return [result, post];
}

export function useUpdateRisk(
  id: number
): [ApiResult<Risk>, (dto: UpdateRiskDto) => void] {
  const { patch: apiPatch, ...result } = useApiPatch<Risk>(`risks/${id}`);
  const post = (dto: UpdateRiskDto) => apiPatch(dto);
  return [result, post];
}

export function useExportRiskDocument(): [
  ApiResult<any>,
  (params: ApiParams & { entity: WorkflowEntity.Risk }) => void,
  (fileName: string) => void
] {
  const { get, ...result } = useApiGet<Risk>(`risks/export`);
  const { response } = result;
  const { setBlob, downloadDocument } = useDownloadBlob(response);

  const getDocument = useCallback(
    (params: ApiParams & { entity: WorkflowEntity.Risk }) => {
      // remove old document
      setBlob(undefined);
      get({ timeZone, params });
    },
    [get]
  );

  return [result, getDocument, downloadDocument];
}
